import * as Sentry from "@sentry/remix";
import {
  ApiError,
  getApiDevosProgressResponse,
  getApiDevosSeriesProgressResponse,
  getPostApiDevosListenEventMutationOptions,
} from "twi-client";

import { analytics } from "~/analytics";
import { queryClient } from "~/entry.client";
import { invalidateEpisodeCardsQueries, optimisticallyUpdateEpisodeCardsQueries } from "~/hooks/usePageSeries";
import { optimisticallyUpdateSeriesProgressQueries } from "~/hooks/useSeriesProgress";
import { EPISODE_COMPLETE_THRESHOLD_PERCENTAGE } from "~/lib/constants";
import { executeMutation } from "~/lib/react-query";
import type { ProgressByEpisodeItem } from "~/lib/series.utils";
import { optimisticallyUpdateProgressQuery } from "~/stores/reactQuery/useProgressQuery";

export type SeriesProgressResponse = {
  seriesId: string;
  devotionals: Array<ProgressByEpisodeItem>;
};

type RawProgressItem = {
  cmsId: string | null;
  progressPercentage?: number;
  lastListenedUtc?: string | null;
  completedAtUtc?: string | null;
};

function normalizeProgressItem(item: RawProgressItem | null): ProgressByEpisodeItem | null {
  if (!item?.cmsId) {
    return null;
  }

  return {
    cmsId: item.cmsId,
    progressPercentage: item.progressPercentage ?? 0,
    lastListenedUtc: item.lastListenedUtc ?? undefined,
    completedAtUtc: item.completedAtUtc ?? undefined,
  };
}

export function normalizeProgressResponse(data: unknown): Array<ProgressByEpisodeItem> {
  const parsed = getApiDevosProgressResponse.safeParse(data);

  if (!parsed.success) {
    console.error("Invalid response format for devo progress", parsed.error);
    return [];
  }

  return parsed.data.flatMap((item) => {
    const normalized = normalizeProgressItem(item);
    return normalized ? [normalized] : [];
  });
}

export function normalizeSeriesProgressResponse(data: unknown, seriesId: string): SeriesProgressResponse | null {
  if (data === undefined || data === null || typeof data === "string") {
    return { seriesId, devotionals: [] };
  }

  const parsed = getApiDevosSeriesProgressResponse.safeParse(data);

  if (!parsed.success) {
    console.error("Invalid response format for series progress", parsed.error);
    return null;
  }

  return {
    seriesId: parsed.data.seriesId ?? seriesId,
    devotionals: (parsed.data.devotionals ?? []).flatMap((item) => {
      const normalized = normalizeProgressItem(item);
      return normalized ? [normalized] : [];
    }),
  };
}

export const ProgressClientService = {
  /**
   * Sends listen progress to the backend. Fails silently so playback isn't impacted.
   */
  logListenProgress: async (cmsId: string, progressPercentage: number): Promise<void> => {
    try {
      const clamped = Math.max(0, Math.min(100, Math.floor(progressPercentage)));

      await executeMutation(queryClient, getPostApiDevosListenEventMutationOptions(), {
        data: { cmsId, progressPercentage: clamped },
      });

      // Optimistically update progress queries with current progress
      updateProgressCaches(cmsId, clamped);
    } catch (error) {
      if (error instanceof ApiError) {
        console.error("Failed to log listen progress", error.status, error.statusText);
        return;
      }

      console.error("Error logging listen progress:", error);
      Sentry.captureException(error, { tags: { context: "logListenProgress" } });
      analytics.track({
        event: "Log Listen Progress Error",
        properties: { error: error instanceof Error ? error.message : String(error) },
      });
    }
  },
};

export function updateProgressCaches(cmsId: string, progressPercentage: number): void {
  const completedAtUtc = progressPercentage === 100 ? new Date().toISOString() : undefined;
  const lastListenedUtc = new Date().toISOString();
  optimisticallyUpdateProgressQuery(cmsId, progressPercentage, completedAtUtc, lastListenedUtc);
  optimisticallyUpdateSeriesProgressQueries(cmsId, progressPercentage, completedAtUtc, lastListenedUtc);
  optimisticallyUpdateEpisodeCardsQueries(cmsId, progressPercentage, completedAtUtc, lastListenedUtc);

  // When episode reaches completion threshold, invalidate episode cards to re-compute "Next Episode"
  if (progressPercentage >= EPISODE_COMPLETE_THRESHOLD_PERCENTAGE) {
    invalidateEpisodeCardsQueries();
  }
}
